import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppSession, ThreadView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { fmtAgo } from '../../lib/format';
import { Icon } from '../../components/HeartMark';
import { Avatar } from '../../components/Bits';

/** The patient's conversation with their care team: messages and reminders in, their own messages out. */
export function MessagesPage({ session, onChanged }: { session: AppSession; onChanged: () => Promise<void> }) {
  const [thread, setThread] = useState<ThreadView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [sentNote, setSentNote] = useState(false);
  const chatRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    try {
      setThread(await api.patientThread(session.token));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load your messages.');
    }
  }, [session.token]);

  useEffect(() => {
    // Opening the conversation reads everything in it, so the bell count refreshes after.
    void load().then(() => onChanged());
    const h = setInterval(() => {
      if (!document.hidden) void load();
    }, 20_000);
    return () => clearInterval(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  useEffect(() => {
    const el = chatRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [thread]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setBusy(true);
    try {
      await api.messageCareTeam(t, session.token);
      setText('');
      setSentNote(true);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send your message.');
    } finally {
      setBusy(false);
    }
  };

  const doctor = thread?.doctorName ?? 'your doctor';

  return (
    <div className="shell-narrow" style={{ margin: '0 auto' }}>
      <div className="page-head">
        <div>
          <h1>Messages</h1>
          <span className="hint">With {thread ? `${thread.doctorName} and your care team at ${thread.practiceName}` : 'your care team'}</span>
        </div>
        <a className="button secondary small" href="#/home">
          <Icon name="back" size={15} /> Back to home
        </a>
      </div>
      {error ? <p className="form-error">{error}</p> : null}
      <section className="card chat-card">
        <header className="chat-head">
          <Avatar name={doctor} size="sm" />
          <div className="row-main">
            <h2>Your care team</h2>
            <span className="hint light small">{thread ? `${thread.doctorName} · ${thread.practiceName}` : 'Loading…'}</span>
          </div>
        </header>
        <div className="chat" ref={chatRef}>
          {thread && thread.messages.length === 0 ? <p className="hint center">No messages yet. Write to {doctor} below: questions about your plan, how a new medication is going, anything they should know.</p> : null}
          {thread?.messages.map((m) => {
            const mine = m.from.role === 'patient';
            return (
              <div key={m.id} className={`bubble-msg ${mine ? 'patient' : ''}`}>
                <div className="msg">
                  {!mine ? (
                    <strong className="msg-from">
                      {m.from.name}
                      {m.kind === 'reminder' ? <span className="badge badge-gold">Reminder</span> : null}
                    </strong>
                  ) : null}
                  <p>{m.text}</p>
                  <span className="msg-meta">{fmtAgo(m.deliveredAt ?? m.createdAt)}</span>
                </div>
              </div>
            );
          })}
        </div>
        <form
          className="composer"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <input type="text" value={text} maxLength={2000} placeholder={`Write to ${doctor}…`} aria-label="Write to your care team" onChange={(e) => setText(e.target.value)} />
          <button type="submit" className="send" disabled={!text.trim() || busy} aria-label="Send">
            <Icon name="chat" size={18} />
          </button>
        </form>
      </section>
      <p className="hint small center" style={{ marginTop: '0.9rem' }}>
        {sentNote ? 'Sent. Your care team reads messages during clinic hours. ' : 'Your care team reads messages during clinic hours. '}
        For chest pain, trouble breathing or fainting, call 911.
      </p>
    </div>
  );
}
